import {
  ChatMessageAttachmentType,
  ChatMessagePayload,
  ChatMessagePayloadAttachment,
  ChatMessagePayloadConference,
} from "@jugl-web/rest-api";
import UserProfileName from "@web-src/features/users/components/UserProfileName";
import { FC, ReactElement, useMemo } from "react";
import { useTranslations } from "@jugl-web/utils";
import { ChatMessageBodyToken } from "@web-src/modules/chats/types";
import { ReactComponent as ChatImage } from "./assets/chat-image.svg";
import { ReactComponent as ChatVideo } from "./assets/chat-video.svg";
import { ReactComponent as ChatAudio } from "./assets/chat-audio.svg";
import { ReactComponent as ChatGif } from "./assets/chat-gif.svg";
import { ReactComponent as ChatDocument } from "./assets/chat-document.svg";
import { ReactComponent as ChatVoice } from "./assets/chat-voice.svg";
import { ReactComponent as ChatAttachment } from "./assets/chat-attachment.svg";
import { ReactComponent as ChatPdf } from "./assets/chat-pdf.svg";
import chatVideoIcon from "./assets/chat-video.png";
import { MessageTokensDisplay } from "../MessageTokensDisplay";
import { getAttachmentsCountLabel } from "../../utils/getAttachmentsCountLabel";
import { clearMessageBody } from "../../utils";
import { isPayloadVersion } from "../../utils/isPayloadVersion";

type ChatListItemLastMessageProps = {
  tokenizedMessage: ChatMessageBodyToken[];
  attachments?: ChatMessagePayloadAttachment[];
  conferenceInfo?: ChatMessagePayloadConference;
  fromUserId?: string;
  isNoHistory?: boolean;
  isDeleted?: boolean;
  deletedBy?: ChatMessagePayload["deleted_by"];
  isUnsupported?: boolean;
  isWorkspaceNotifications?: boolean;
  isSelf?: boolean;
  isDraft?: boolean;
  payloadVersion: number;
  isGroupInfo?: boolean;
};

export const ChatListItemLastMessage: FC<ChatListItemLastMessageProps> = ({
  tokenizedMessage,
  attachments,
  conferenceInfo,
  fromUserId,
  isNoHistory,
  isDeleted,
  deletedBy,
  isUnsupported,
  isWorkspaceNotifications,
  isSelf,
  isDraft,
  payloadVersion,
  isGroupInfo,
}) => {
  const { t } = useTranslations();
  const attachmentInfo: {
    icon: ReactElement;
  } | null = useMemo(() => {
    if (conferenceInfo) {
      return {
        icon: (
          <img
            src={chatVideoIcon}
            alt="video"
            className="relative bottom-0.5 inline-block h-[16px] w-[16px]"
          />
        ),
      };
    }

    const firstAttachment = attachments?.[0];

    if (!firstAttachment) {
      return null;
    }

    switch (firstAttachment.type) {
      case ChatMessageAttachmentType.image:
        return {
          icon: <ChatImage />,
        };
      case ChatMessageAttachmentType.audio:
        return {
          icon: <ChatAudio />,
        };
      case ChatMessageAttachmentType.video:
        return {
          icon: <ChatVideo />,
        };
      case ChatMessageAttachmentType.doc:
        return firstAttachment.mimetype.search("pdf") !== -1
          ? {
              icon: <ChatPdf />,
            }
          : {
              icon: <ChatDocument />,
            };
      case ChatMessageAttachmentType.otherFile:
        return {
          icon: <ChatAttachment />,
        };
      case ChatMessageAttachmentType.voice:
        return {
          icon: <ChatVoice />,
        };
      case ChatMessageAttachmentType.gif:
      case ChatMessageAttachmentType.gifDirectUrl:
        return {
          icon: <ChatGif />,
        };
      default:
        return null;
    }
  }, [attachments, conferenceInfo]);

  if (isWorkspaceNotifications) {
    return t({
      id: "chats-page.workspace-notifications",
      defaultMessage: "Workspace Notifications",
    });
  }

  if (isDeleted) {
    return deletedBy === "admin"
      ? t({
          id: "chats-page.message-deleted-by-admin-of-group",
          defaultMessage: "Message has been deleted by Admin of the Group",
        })
      : t({
          id: "chats-page.message-has-been-deleted",
          defaultMessage: "Message has been deleted",
        });
  }

  if (isUnsupported) {
    return t({
      id: "chats-page.unsupported-message-format",
      defaultMessage: "Unsupported message format",
    });
  }

  if (isNoHistory) {
    return isSelf
      ? t({
          id: "chats-page.hey-welcome-to-jugl",
          defaultMessage: "Hey! Welcome to Jugl 👋",
        })
      : t({
          id: "chats-page.no-chat-history-yet",
          defaultMessage: "No Chat History yet 👋",
        });
  }
  if (!attachmentInfo && !tokenizedMessage?.length) {
    return t({
      id: "chats-page.no-body",
      defaultMessage: "No body",
    });
  }

  return (
    <>
      {fromUserId && !isDraft && (
        <UserProfileName userId={fromUserId} suffix=": " />
      )}
      {isDraft && (
        <span className="text-tertiary-800">
          {t({
            id: "chats-page.draft",
            defaultMessage: "Draft",
          })}
          {": "}
        </span>
      )}
      {attachmentInfo && (
        <>
          <span className="mr-1 h-[16px]">{attachmentInfo.icon}</span>
        </>
      )}
      {(() => {
        if (conferenceInfo) {
          return (
            conferenceInfo.name ||
            t({
              id: "common.join-video-meeting",
              defaultMessage: "Join video meeting",
            })
          );
        }
        if (tokenizedMessage.length) {
          return (
            <MessageTokensDisplay
              tokens={tokenizedMessage}
              isGroupInfo={isGroupInfo}
            />
          );
        }

        if (attachments && attachments.length) {
          if (attachments.length > 1) {
            return getAttachmentsCountLabel(
              attachments[0].type,
              attachments.length,
              t
            );
          }
          return (
            (isPayloadVersion(1, payloadVersion)
              ? clearMessageBody(attachments[0].caption || "")
              : "") ||
            attachments[0].name ||
            t({
              id: "chats-page.file",
              defaultMessage: "File",
            })
          );
        }

        return "";
      })()}
    </>
  );
};
